'use client';

import { useTranslations } from 'next-intl';
import { Link } from '@/i18n/routing';
import { Pumptrack } from '@/types';
import SocialShare from './SocialShare';
import FavoriteButton from './FavoriteButton';
import ReviewsList from './ReviewsList';
import ReviewForm from './ReviewForm';
import {
  MapPin,
  Star,
  Ruler,
  Target,
  Lightbulb,
  Moon,
  Trees,
  Construction,
  Home,
  ChevronRight,
  ExternalLink,
  ArrowRight,
  Shield
} from 'lucide-react';

interface PumptrackDetailProps {
  pumptrack: Pumptrack;
  locale: string;
}

export default function PumptrackDetail({ pumptrack, locale }: PumptrackDetailProps) {
  const t = useTranslations('pumptrackDetail');

  const difficultyColors: Record<string, string> = {
    easy: '#22c55e',
    medium: '#f59e0b',
    hard: '#ef4444',
  };

  const difficultyLabels: Record<string, string> = {
    easy: 'Łatwy',
    medium: 'Średni',
    hard: 'Trudny',
  };

  const surfaceLabels: Record<string, string> = {
    asphalt: 'Asfalt',
    concrete: 'Beton',
    dirt: 'Ziemia',
    gravel: 'Żwir',
  };

  return (
    <div className="pumptrack-page">
      {/* Hero */}
      <section className="pumptrack-hero">
        <div className="pumptrack-hero-inner">
          {/* Breadcrumb */}
          <nav className="pumptrack-breadcrumb">
            <Link href="/"><Home size={14} /> {t('home')}</Link>
            <ChevronRight size={14} />
            <Link href="/pumptracki">{t('pumptracks')}</Link>
            <ChevronRight size={14} />
            <span>{pumptrack.name}</span>
          </nav>

          {/* Title */}
          <h1 className="pumptrack-title">{pumptrack.name}</h1>

          {/* Location */}
          {pumptrack.region && (
            <p className="pumptrack-location">
              <MapPin size={16} />
              {pumptrack.region}
            </p>
          )}

          {/* Stats Bar */}
          <div className="pumptrack-stats-bar">
            {/* Difficulty */}
            {pumptrack.difficulty && (
              <div className="stat-item stat-difficulty">
                <div className="stat-icon" style={{ background: difficultyColors[pumptrack.difficulty] || '#10b981' }}>
                  <Target size={20} color="#fff" />
                </div>
                <div className="stat-text">
                  <span className="stat-value" style={{ color: difficultyColors[pumptrack.difficulty] }}>
                    {difficultyLabels[pumptrack.difficulty] || pumptrack.difficulty}
                  </span>
                  <span className="stat-label">{t('difficulty')}</span>
                </div>
              </div>
            )}

            {/* Surface */}
            {pumptrack.surface && (
              <div className="stat-item">
                <div className="stat-icon">
                  <Ruler size={20} />
                </div>
                <div className="stat-text">
                  <span className="stat-value">{surfaceLabels[pumptrack.surface] || pumptrack.surface}</span>
                  <span className="stat-label">{t('surface')}</span>
                </div>
              </div>
            )}

            {/* Lighting */}
            <div className="stat-item">
              <div className="stat-icon">
                {pumptrack.lighting ? <Lightbulb size={20} /> : <Moon size={20} />}
              </div>
              <div className="stat-text">
                <span className="stat-value">{pumptrack.lighting ? t('yes') : t('no')}</span>
                <span className="stat-label">{t('lighting')}</span>
              </div>
            </div>

            {/* Fencing */}
            <div className="stat-item">
              <div className="stat-icon">
                {pumptrack.fenced ? <Trees size={20} /> : <Construction size={20} />}
              </div>
              <div className="stat-text">
                <span className="stat-value">{pumptrack.fenced ? t('yes') : t('no')}</span>
                <span className="stat-label">{t('fencing')}</span>
              </div>
            </div>

            {/* Rating */}
            {pumptrack.rating !== undefined && pumptrack.rating > 0 && (
              <div className="stat-item">
                <div className="stat-icon stat-icon-rating">
                  <Star size={20} fill="#fbbf24" color="#fbbf24" />
                </div>
                <div className="stat-text">
                  <span className="stat-value">{pumptrack.rating}/5</span>
                  <span className="stat-label">{t('rating')}</span>
                </div>
              </div>
            )}

            {/* Free badge */}
            {pumptrack.free && (
              <div className="stat-item stat-free">
                <div className="stat-icon stat-icon-free">
                  <span>✓</span>
                </div>
                <div className="stat-text">
                  <span className="stat-value">{t('freeEntry')}</span>
                  <span className="stat-label">Wstęp</span>
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="pumptrack-actions">
            <FavoriteButton
              id={pumptrack.documentId}
              slug={pumptrack.slug}
              name={pumptrack.name}
              type="pumptrack"
            />
          </div>
        </div>
      </section>

      {/* Content */}
      <div className="pumptrack-content">
        <div className="pumptrack-layout">
          {/* Main */}
          <main className="pumptrack-main">
            {/* Description */}
            {(pumptrack.blurb || pumptrack.description) && (
              <div className="pumptrack-card">
                <h2>{t('about')}</h2>
                <p className="pumptrack-description">{pumptrack.blurb || pumptrack.description}</p>
              </div>
            )}

            {/* Reviews */}
            <ReviewsList parkId={pumptrack.documentId} parkType="pumptrack" />
            <ReviewForm parkId={pumptrack.documentId} parkType="pumptrack" parkName={pumptrack.name} />
          </main>

          {/* Sidebar */}
          <aside className="pumptrack-sidebar">
            {/* Info card */}
            <div className="sidebar-card">
              <h3>{t('information')}</h3>

              {pumptrack.lat && pumptrack.lng && (
                <div className="detail-row">
                  <MapPin size={16} />
                  <span>{t('location')}</span>
                  <strong>{pumptrack.lat.toFixed(4)}, {pumptrack.lng.toFixed(4)}</strong>
                </div>
              )}

              {pumptrack.rating !== undefined && pumptrack.rating > 0 && (
                <div className="detail-row">
                  <Star size={16} />
                  <span>{t('rating')}</span>
                  <strong>{pumptrack.rating}/5</strong>
                </div>
              )}

              {/* Links */}
              <div className="sidebar-links">
                {pumptrack.lat && pumptrack.lng && (
                  <a
                    href={`https://www.google.com/maps?q=${pumptrack.lat},${pumptrack.lng}`}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="sidebar-link"
                  >
                    <ExternalLink size={16} /> {t('openInMaps')}
                  </a>
                )}
                {pumptrack.url && (
                  <a
                    href={pumptrack.url}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="sidebar-link sidebar-link-primary"
                  >
                    <ExternalLink size={16} /> {t('visitWebsite')}
                  </a>
                )}
              </div>
            </div>

            {/* Affiliate Gear Card */}
            <div className="sidebar-card gear-card">
              <h3><Shield size={16} /> {locale === 'pl' ? 'Polecane Kaski MTB' : 'Recommended MTB Helmets'}</h3>
              <p className="gear-intro">
                {locale === 'pl'
                  ? 'Bezpieczeństwo przede wszystkim! Wybierz sprawdzony kask:'
                  : 'Safety first! Choose a proven helmet:'}
              </p>
              <div className="gear-list">
                <a
                  href="https://amzn.to/4rM7sjl"
                  target="_blank"
                  rel="noopener sponsored"
                  className="gear-item"
                >
                  <span className="gear-name">MTB Enduro 2.0</span>
                  <span className="gear-badge">{locale === 'pl' ? 'Bestseller' : 'Bestseller'}</span>
                </a>
                <a
                  href="https://amzn.to/4a68yAf"
                  target="_blank"
                  rel="noopener sponsored"
                  className="gear-item"
                >
                  <span className="gear-name">Leatt Gravity 1.0</span>
                  <span className="gear-badge">{locale === 'pl' ? 'Enduro' : 'Enduro'}</span>
                </a>
                <a
                  href="https://amzn.to/3MkA4jH"
                  target="_blank"
                  rel="noopener sponsored"
                  className="gear-item"
                >
                  <span className="gear-name">Leatt MTB 4.0</span>
                  <span className="gear-badge">{locale === 'pl' ? 'Premium' : 'Premium'}</span>
                </a>
              </div>
              <p className="gear-disclosure">
                {locale === 'pl'
                  ? '* Linki afiliacyjne Amazon'
                  : '* Amazon affiliate links'}
              </p>
            </div>

            {/* Back to pumptracks */}
            <div className="sidebar-card back-card">
              <h3>{t('pumptracks')}</h3>
              <p className="back-text">Wróć do mapy pumptracków</p>
              <Link href="/pumptracki" className="back-link">
                {t('backToMap')} <ArrowRight size={16} />
              </Link>
            </div>
          </aside>
        </div>
      </div>

      <style jsx global>{`
        .pumptrack-page {
          min-height: 100vh;
          background: var(--bg);
        }

        /* Hero */
        .pumptrack-hero {
          padding: 100px 20px 40px;
          background: linear-gradient(135deg, var(--accent) 0%, #059669 100%);
        }

        .pumptrack-hero-inner {
          max-width: 1100px;
          margin: 0 auto;
        }

        .pumptrack-breadcrumb {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 20px;
          font-size: 14px;
          color: rgba(255, 255, 255, 0.8);
        }

        .pumptrack-breadcrumb a {
          color: rgba(255, 255, 255, 0.8);
          text-decoration: none;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .pumptrack-breadcrumb a:hover {
          color: #ffffff;
        }

        .pumptrack-breadcrumb svg {
          color: rgba(255, 255, 255, 0.5);
        }

        .pumptrack-breadcrumb > span:last-child {
          color: #ffffff;
          font-weight: 500;
        }

        .pumptrack-title {
          font-size: clamp(28px, 5vw, 40px);
          font-weight: 800;
          color: #ffffff;
          margin: 0 0 12px;
          text-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        .pumptrack-location {
          display: flex;
          align-items: center;
          gap: 6px;
          color: rgba(255, 255, 255, 0.9);
          font-size: 16px;
          margin: 0 0 24px;
        }

        /* Stats Bar */
        .pumptrack-stats-bar {
          display: flex;
          flex-wrap: wrap;
          gap: 16px;
          padding: 20px;
          background: var(--panel);
          border-radius: 16px;
          border: 1px solid var(--border);
          box-shadow: 0 2px 12px rgba(0,0,0,0.06);
          margin-bottom: 20px;
        }

        .stat-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 8px 16px 8px 8px;
          background: var(--bg);
          border-radius: 12px;
          min-width: 140px;
        }

        .stat-difficulty {
          min-width: 160px;
        }

        .stat-free {
          background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
        }

        .stat-icon {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          background: var(--accent);
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .stat-icon-rating {
          background: #fef3c7;
        }

        .stat-icon-free {
          background: #22c55e;
          font-size: 18px;
          font-weight: bold;
        }

        .stat-text {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .stat-value {
          font-size: 16px;
          font-weight: 700;
          color: var(--text);
        }

        .stat-label {
          font-size: 12px;
          color: var(--muted);
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .pumptrack-actions {
          margin-top: 16px;
        }

        /* Content */
        .pumptrack-content {
          max-width: 1100px;
          margin: 0 auto;
          padding: 32px 20px;
        }

        .pumptrack-layout {
          display: grid;
          grid-template-columns: 1fr 320px;
          gap: 32px;
        }

        @media (max-width: 900px) {
          .pumptrack-layout {
            grid-template-columns: 1fr;
          }
        }

        .pumptrack-main {
          display: flex;
          flex-direction: column;
          gap: 24px;
        }

        .pumptrack-card {
          background: var(--panel);
          border-radius: 16px;
          padding: 24px;
          border: 1px solid var(--border);
        }

        .pumptrack-card h2 {
          font-size: 18px;
          font-weight: 700;
          color: var(--text);
          margin: 0 0 16px;
          padding-bottom: 12px;
          border-bottom: 1px solid var(--border);
        }

        .pumptrack-description {
          font-size: 15px;
          line-height: 1.7;
          color: var(--text-weak);
          margin: 0;
        }

        /* Sidebar */
        .pumptrack-sidebar {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .sidebar-card {
          background: var(--panel);
          border-radius: 16px;
          padding: 20px;
          border: 1px solid var(--border);
        }

        .sidebar-card h3 {
          font-size: 15px;
          font-weight: 700;
          color: var(--text);
          margin: 0 0 16px;
          padding-bottom: 12px;
          border-bottom: 1px solid var(--border);
        }

        .detail-row {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 0;
          border-bottom: 1px solid var(--border);
          font-size: 14px;
          color: var(--text-weak);
        }

        .detail-row:last-of-type {
          border-bottom: none;
        }

        .detail-row svg {
          color: var(--accent);
          flex-shrink: 0;
        }

        .detail-row span {
          flex: 1;
        }

        .detail-row strong {
          color: var(--text);
          font-weight: 600;
        }

        .sidebar-links {
          display: flex;
          flex-direction: column;
          gap: 10px;
          margin-top: 16px;
        }

        .sidebar-link {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 16px;
          background: var(--bg);
          border-radius: 10px;
          color: var(--text);
          text-decoration: none;
          font-size: 14px;
          font-weight: 500;
          transition: all 0.2s;
          border: 1px solid var(--border);
        }

        .sidebar-link:hover {
          background: var(--accent);
          color: #fff;
          border-color: var(--accent);
        }

        .sidebar-link-primary {
          background: var(--accent);
          color: #fff;
          border-color: var(--accent);
        }

        .sidebar-link-primary:hover {
          background: #059669;
          border-color: #059669;
        }

        /* Back card */
        .back-card {
          background: linear-gradient(135deg, var(--accent), #059669);
          border: none;
        }

        .back-card h3 {
          color: rgba(255,255,255,0.9);
          border-color: rgba(255,255,255,0.2);
        }

        .back-text {
          color: rgba(255,255,255,0.8);
          font-size: 14px;
          margin: 0 0 16px;
        }

        .back-link {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 12px 20px;
          background: rgba(255,255,255,0.2);
          border-radius: 10px;
          color: #fff;
          text-decoration: none;
          font-weight: 600;
          font-size: 14px;
          transition: background 0.2s;
        }

        .back-link:hover {
          background: rgba(255,255,255,0.3);
        }

        /* Gear Card */
        .gear-card {
          background: linear-gradient(135deg, #fefce8 0%, #fef9c3 100%);
          border-color: #fde047;
        }

        .gear-card h3 {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #854d0e;
          border-color: #fde047;
        }

        .gear-intro {
          font-size: 13px;
          color: #a16207;
          margin: 0 0 12px;
          line-height: 1.5;
        }

        .gear-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .gear-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 10px 12px;
          background: #ffffff;
          border-radius: 8px;
          text-decoration: none;
          border: 1px solid #fde047;
          transition: all 0.2s;
        }

        .gear-item:hover {
          background: #16a34a;
          border-color: #16a34a;
        }

        .gear-item:hover .gear-name,
        .gear-item:hover .gear-badge {
          color: #ffffff;
        }

        .gear-name {
          font-size: 13px;
          font-weight: 600;
          color: #374151;
          transition: color 0.2s;
        }

        .gear-badge {
          font-size: 11px;
          font-weight: 600;
          color: #16a34a;
          background: #dcfce7;
          padding: 3px 8px;
          border-radius: 4px;
          transition: all 0.2s;
        }

        .gear-item:hover .gear-badge {
          background: rgba(255,255,255,0.2);
        }

        .gear-disclosure {
          font-size: 11px;
          color: #a16207;
          margin: 12px 0 0;
          opacity: 0.8;
        }

        @media (max-width: 600px) {
          .pumptrack-stats-bar {
            flex-direction: column;
            gap: 12px;
          }

          .stat-item {
            min-width: auto;
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
}
